import { isClientFeatureEnabled } from '@wh/feature-toggle-detector';

import MessageBusTopics from '../../common/MessageBusTopics';
import { get } from '../../utils';

const {
    SESSION_COMMAND_STATE_GET,
    SESSION_EVENT_STATE_GET,
    SESSION_EVENT_STATE_CHANGED,
    HIDDEN_MARKETS_COMMAND_START,
    HIDDEN_MARKETS_COMMAND_STATE_GET,
    HIDDEN_MARKETS_COMMAND_STOP,
    HIDDEN_MARKETS_EVENT_START,
    HIDDEN_MARKETS_EVENT_STATE_GET,
    HIDDEN_MARKETS_EVENT_STOP,
} = MessageBusTopics;
const { subscribe, unsubscribe, publish } = window.WH.messageBus;

function getConfig() {
    return get(window, 'whStatic.config.hiddenMarkets', {
        enabled: false,
    });
}

const hiddenMarketsWidgetStates = {
    STOPPED: 'STOPPED',
};

const sessionManagerStates = {
    LOGGED_IN: 'LOGGED_IN',
    NOT_LOGGED_IN: 'NOT_LOGGED_IN',
    UNKNOWN: 'UNKNOWN',
};

const {
    enabled,
    hiddenLive,
    queryFeatureToggleEnabled,
    containerId,
} = getConfig();

let sessionSubscription = null;

function isHiddenMarketsActive() {
    if (!enabled) return false;

    if (hiddenLive) {
        return isClientFeatureEnabled(queryFeatureToggleEnabled, 'public');
    }

    return true;
}

function runMessageBusCommand(commandTopic, eventTopic, payload) {
    return new Promise((resolve, reject) => {
        try {
            const subscription = subscribe(eventTopic, (topic, payload) => {
                if (topic !== eventTopic) return;
                const { error } = payload;
                unsubscribe(subscription);
                if (error) {
                    reject(payload);
                } else {
                    resolve(payload);
                }
            });
            publish(commandTopic, payload);
        } catch (err) {
            reject(err);
        }
    });
}

async function startFlow() {
    const { state } = await getHiddenMarketsState();

    if (state !== hiddenMarketsWidgetStates.STOPPED) {
        await stopHiddenMarkets();
    }

    await startHiddenMarkets();
}

async function stopFlow() {
    const { state } = await getHiddenMarketsState();

    if (state !== hiddenMarketsWidgetStates.STOPPED) {
        await stopHiddenMarkets();
    }
}

function subscribeToSessionStateChanges() {
    sessionSubscription = subscribe(SESSION_EVENT_STATE_CHANGED, async (topic, { state }) => {
        try {
            if (state === sessionManagerStates.LOGGED_IN) {
                await startFlow();
            } else if (state === sessionManagerStates.NOT_LOGGED_IN) {
                await stopFlow();
            }
        } catch (err) {
            console.error(err);
        }
    });
}

function unsubscribeFromSessionStateChanges() {
    if (sessionSubscription !== null) {
        unsubscribe(sessionSubscription);
        sessionSubscription = null;
    }
}

function getSessionState() {
    return runMessageBusCommand(SESSION_COMMAND_STATE_GET, SESSION_EVENT_STATE_GET);
}

function getHiddenMarketsState() {
    return runMessageBusCommand(HIDDEN_MARKETS_COMMAND_STATE_GET, HIDDEN_MARKETS_EVENT_STATE_GET);
}

function startHiddenMarkets() {
    return runMessageBusCommand(HIDDEN_MARKETS_COMMAND_START, HIDDEN_MARKETS_EVENT_START, {
        containerSelector: `#${containerId}`,
    });
}

function stopHiddenMarkets() {
    return runMessageBusCommand(HIDDEN_MARKETS_COMMAND_STOP, HIDDEN_MARKETS_EVENT_STOP);
}

async function start() {
    try {
        if (!isHiddenMarketsActive()) return;

        subscribeToSessionStateChanges();

        const { state } = await getSessionState();
        if (state === sessionManagerStates.LOGGED_IN) {
            await startFlow();
        }
    } catch (err) {
        console.error(err);
    }
}

async function stop() {
    try {
        if (!isHiddenMarketsActive()) return;

        unsubscribeFromSessionStateChanges();

        await stopFlow();
    } catch (err) {
        console.error(err);
    }
}

export default {
    start,
    stop,
};
